import { useState, useContext, useEffect, useRef, useMemo } from "react";
import { Link, Navigate } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { IconAffiliate, IconBuildingStore, IconLayoutDashboard } from "@tabler/icons-react";
import { useBrandAuth } from "../../hooks/useBrandAuth";
import {
  brandService,
  BrandAnalyticsResponse,
  LatestAffiliateSale,
  OnboardingStatusResponse,
} from "../../services/brand.service";
import { programService } from "../../services/program.service";
import { payoutService } from "../../services/payout.service";
import PageHeader from "../../components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { BrandContext } from "../../contexts/BrandContext/context";
import { toast } from "react-hot-toast";
import { TopAffiliateTable } from "./dashboard/top-affiliate-table";
import { ActivityStrip } from "./dashboard/activity-strip";
import { NetworkCard } from "./dashboard/network-card";
import { ApplicationsCard } from "./dashboard/applications-card";
import { PayoutRequestsCard } from "./dashboard/payout-requests-card";
import { ProgramsCard } from "./dashboard/programs-card";
import { TopAffiliatesCard } from "./dashboard/top-affiliates-card";
import { TopProductsCard } from "./dashboard/top-products-card";
import { DashboardNav } from "./dashboard/dashboard-nav";
import { DashboardSection } from "./dashboard/dashboard-section";
import { TikTokRow } from "./dashboard/tiktok-row";
import { useTikTokSectionVisible } from "./dashboard/use-tiktok-section";
import { useTikTokAccess } from "@/hooks/useTikTokAccess";
import { BetaBadge } from "./tiktok/pre-release";
import TikTokIcon from "@/assets/tiktok";
import { DashboardSkeleton } from "./dashboard/DashboardSkeleton";
import { usePermissions } from "@/contexts/PermissionsContext";
import { PlanFeature } from "@/types/permissions";
import type { Program } from "@/types/program";
import {
  buildOnboardingSteps,
  clearOnboardingSkip,
  hasSkippedOnboardingToDashboard,
  isOnboardingComplete,
} from "@/lib/brandOnboarding";
import { SHOW_ONBOARDING } from "@/config/features";
import { BrandPayoutMethodsPrompt } from "@/components/brand/payouts/BrandPayoutMethodsPrompt";

export function Dashboard() {
  useContext(BrandContext);
  useBrandAuth();
  const { hasFeature, isLoading: permissionsLoading } = usePermissions();
  const [analyticsData, setAnalyticsData] =
    useState<BrandAnalyticsResponse | null>(null);
  const [onboardingData, setOnboardingData] =
    useState<OnboardingStatusResponse | null>(null);
  const [onboardingLoaded, setOnboardingLoaded] = useState(!SHOW_ONBOARDING);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [statsDays, setStatsDays] = useState<number>(30);
  const [latestSales, setLatestSales] = useState<LatestAffiliateSale[]>([]);
  // Guards against a slow response for a previously selected period
  // overwriting the currently selected one
  const latestRequestedDays = useRef(statsDays);

  const hasPostCheckout = hasFeature(PlanFeature.POST_CHECKOUT_REFERRALS);
  const tiktokVisible = useTikTokSectionVisible();
  // PRE_RELEASE_TIKTOK: the nav chip wears the same Beta badge as the section.
  const tiktokBeta = useTikTokAccess().features.analytics === "beta";

  // The list cards load on their own so a slow one never holds the page.
  const programsQuery = useQuery<Program[]>({
    queryKey: ["my-programs"],
    queryFn: () => programService.getMyPrograms(),
  });
  const affiliateCountsQuery = useQuery({
    queryKey: ["program-affiliate-counts"],
    queryFn: () => programService.getAffiliateCountsForPrograms(),
    enabled: (programsQuery.data?.length ?? 0) > 0,
  });
  const applicationsQuery = useQuery({
    queryKey: ["dashboard-applications"],
    queryFn: () => programService.getApplications({ page: 1, limit: 5 }),
  });
  const payoutRequestsQuery = useQuery({
    queryKey: ["dashboard-payout-requests"],
    queryFn: () => payoutService.getPayoutRequests(1, 5),
  });
  // Period tables: top affiliates, top products, countries and the figures
  // behind every card's "Stats" flip. The previous period's data stays on
  // screen while a new period loads.
  const tablesQuery = useQuery({
    queryKey: ["dashboard-tables", statsDays],
    queryFn: () => brandService.getDashboardTables(statsDays),
    placeholderData: keepPreviousData,
  });
  const tables = tablesQuery.data;

  const onboardingSteps = useMemo(
    () => buildOnboardingSteps(onboardingData, hasPostCheckout),
    [onboardingData, hasPostCheckout]
  );

  const onboardingDone =
    !SHOW_ONBOARDING || isOnboardingComplete(onboardingSteps);
  const skippedToDashboard = hasSkippedOnboardingToDashboard();

  const fetchBrandAnalytics = async (days: number, isInitialLoad: boolean) => {
    latestRequestedDays.current = days;
    if (isInitialLoad) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }
    try {
      const analytics = await brandService.getBrandAnalytics(days);
      if (latestRequestedDays.current !== days) return;
      setAnalyticsData(analytics);
    } catch (error) {
      console.error("Failed to fetch brand analytics:", error);
      toast.error("Failed to load brand analytics");
    } finally {
      if (latestRequestedDays.current === days) {
        setLoading(false);
        setIsRefreshing(false);
      }
    }
  };

  const fetchOnboardingStatus = async () => {
    try {
      const onboarding = await brandService.getOnboardingStatus();
      setOnboardingData(onboarding);
    } catch (error) {
      console.error("Failed to fetch onboarding status:", error);
    } finally {
      setOnboardingLoaded(true);
    }
  };

  const fetchLatestSales = async () => {
    try {
      const sales = await brandService.getLatestAffiliateSales();
      setLatestSales(sales);
    } catch (error) {
      console.error("Failed to fetch latest sales:", error);
      toast.error("Failed to load latest affiliate sales");
    }
  };

  useEffect(() => {
    fetchBrandAnalytics(30, true);
    if (SHOW_ONBOARDING) fetchOnboardingStatus();
    fetchLatestSales();
  }, []);

  const handleStatsPeriodChange = (days: number) => {
    if (days === statsDays || isRefreshing) return;
    setStatsDays(days);
    fetchBrandAnalytics(days, false);
  };

  if (
    onboardingLoaded &&
    !permissionsLoading &&
    !onboardingDone &&
    !skippedToDashboard
  ) {
    return <Navigate to="/brand/onboarding" replace />;
  }

  if (loading || !onboardingLoaded || permissionsLoading) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="flex-1 space-y-4">
      {!onboardingDone && (
        <div className="flex flex-col gap-3 rounded-lg border border-brand-600/30 bg-brand-600/5 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-medium text-foreground">
            Finish setting up your brand
          </p>
          <Button asChild variant="outline">
            <Link to="/brand/onboarding" onClick={() => clearOnboardingSkip()}>
              Continue setup
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      )}
      {/* Payout methods are not collected at signup. Ask once there is a
          reason: an affiliate has joined or a sale has landed. */}
      <BrandPayoutMethodsPrompt
        when={
          (analyticsData?.totalAffilates?.totalAffilates ?? 0) > 0 ||
          (analyticsData?.totalAffiliateOrders?.totalOrders ?? 0) > 0 ||
          latestSales.length > 0
        }
        body="Affiliates are joining. Pick at least one payout method now so their first payout request can be paid without delay."
      />
      {/* The header and the section nav pin together at the viewport top.
          The negative top margin swallows <main>'s own top padding and the
          unit re-creates it inside, so it rests already pinned and never
          travels before sticking; sections scroll underneath (scroll-mt-36
          on each anchor keeps their titles clear of this bar). */}
      <div className="sticky top-0 z-30 -mx-1 -mt-16 space-y-3 bg-background px-1 pb-3 pt-16 md:-mt-8 md:pt-8">
        <PageHeader title="Dashboard" showDefaultActions={true} />
        <DashboardNav
          sections={[
          {
            id: "dashboard-overview",
            label: "Overview",
            icon: <IconLayoutDashboard className="size-4" stroke={1.75} />,
            hint: "The whole business at a glance",
          },
          {
            id: "reveshare-analytics",
            label: "Reveshare",
            icon: <IconAffiliate className="size-4" stroke={1.75} />,
            hint: "Is my program healthy? What needs me today?",
          },
          {
            id: "shopify-analytics",
            label: "Shopify",
            icon: <IconBuildingStore className="size-4" stroke={1.75} />,
            hint: "What are my affiliates selling in my store?",
          },
          ...(tiktokVisible
            ? [
                {
                  id: "tiktok-analytics",
                  label: "TikTok Shop",
                  icon: <TikTokIcon className="size-4" />,
                  hint: "What's happening on my TikTok Shop?",
                  // PRE_RELEASE_TIKTOK
                  badge: tiktokBeta ? <BetaBadge floating size="sm" className="-right-1 -top-2" /> : undefined,
                },
              ]
            : []),
          ]}
        />
      </div>
      <div id="dashboard-overview" className="scroll-mt-36">
        <ActivityStrip
          analytics={analyticsData}
          periodDays={statsDays}
          onPeriodChange={handleStatsPeriodChange}
          isRefreshing={isRefreshing}
        />
      </div>

      <DashboardSection
        id="reveshare-analytics"
        icon={<IconAffiliate className="size-5 text-foreground" stroke={1.75} />}
        title="Reveshare"
        description="Is my program healthy? Applications waiting, payouts due, and how your network is growing."
      >
        {/* The two queues first — they are the "what needs me today" cards —
            at their natural height, never stretched into internal scrolling. */}
        <div className="grid items-start gap-4 lg:grid-cols-2">
          <ApplicationsCard
            data={applicationsQuery.data?.data}
            stats={tables?.stats.applications}
            isLoading={applicationsQuery.isLoading}
          />
          <PayoutRequestsCard
            data={payoutRequestsQuery.data?.data}
            stats={tables?.stats.payouts}
            isLoading={payoutRequestsQuery.isLoading}
          />
        </div>
        <div className="grid gap-4 lg:grid-cols-12">
          <div className="min-w-0 lg:col-span-4">
            <NetworkCard
              analytics={analyticsData}
              programs={programsQuery.data}
              countries={tables?.countries}
              stats={tables?.stats.network}
              topProduct={tables?.topProducts[0] ?? null}
              periodDays={statsDays}
            />
          </div>
          {/* The network card sets this row's height; the programs card is
              taken out of flow (absolute) so it fills exactly that height
              and scrolls inside when there are more programs. */}
          <div className="relative min-w-0 lg:col-span-8">
            <div className="lg:absolute lg:inset-0">
              <ProgramsCard
                programs={programsQuery.data}
                affiliateCounts={affiliateCountsQuery.data}
                stats={tables?.stats.programs}
                isLoading={programsQuery.isLoading}
              />
            </div>
          </div>
        </div>
      </DashboardSection>

      <DashboardSection
        id="shopify-analytics"
        icon={<IconBuildingStore className="size-5 text-foreground" stroke={1.75} />}
        title="Shopify"
        description="What are affiliates selling in your store? Latest sales, top performers and products."
      >
        {/* Fixed height on large screens so the sales table scrolls inside
            its card instead of growing with every new sale. */}
        <div className="lg:h-96">
          <TopAffiliateTable data={latestSales} stats={tables?.stats.sales} />
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <TopAffiliatesCard
            data={tables?.topAffiliates}
            stats={tables?.stats.affiliates}
            isLoading={tablesQuery.isLoading}
          />
          <TopProductsCard
            data={tables?.topProducts}
            stats={tables?.stats.products}
            isLoading={tablesQuery.isLoading}
          />
        </div>
      </DashboardSection>

      <TikTokRow
        shopifyByDay={analyticsData?.totalAffiliateRevenue?.revenueByDay}
        days={statsDays}
      />
    </div>
  );
}

export default Dashboard;
